import { computed, type ComputedRef, ref, watch, type Ref } from 'vue'

type UseFormDirtySnapshotOptions<T> = {
  showOverlay: Ref<boolean> | ComputedRef<boolean>
  currentValue: Ref<T> | ComputedRef<T>
  /** Appelé quand l’overlay s’ouvre ou après chargement async pour figer l’état initial. */
  onCaptureSnapshot?: () => void
  hasUnsavedChanges: Ref<boolean>
}

export function useFormDirtySnapshot<T>(options: UseFormDirtySnapshotOptions<T>) {
  const initialSnapshot = ref('')

  function captureSnapshot() {
    initialSnapshot.value = serializeFormSnapshot(options.currentValue.value)
    options.onCaptureSnapshot?.()
  }

  const isFormDirty = computed(() => isSnapshotDirty(initialSnapshot.value, options.currentValue.value))

  watch(isFormDirty, (dirty) => {
    options.hasUnsavedChanges.value = dirty
  }, { immediate: true })

  watch(
    () => options.showOverlay.value,
    (show) => {
      if (show) return
      initialSnapshot.value = ''
      options.hasUnsavedChanges.value = false
    },
  )

  return {
    initialSnapshot,
    isFormDirty,
    captureSnapshot,
  }
}

function serializeFormSnapshot<T>(value: T): string {
  return JSON.stringify(value)
}

function isSnapshotDirty<T>(initialSnapshot: string, currentValue: T): boolean {
  if (!initialSnapshot) return false
  return serializeFormSnapshot(currentValue) !== initialSnapshot
}
